import { useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { updateUserData } from '../../profile/userSlice';

export enum CustomFeatureClient {
  PAM = 'pam',
}

export enum AccessLevel {
  User = '1',
  FriendsAndFamily = '2',
  Leadership = '3',
  ChildClientAdmin = '4',
  ParentClientAdmin = '5',
  Translation = '8',
  LimitedAdmin = '9',
  Admin = '10',
}
interface SSO {
  _id: string;
  clientId: string;
  type: string;
  platform: string;
  redirect_uri?: string;
  id?: string;
  scope?: string;
  issuer?: string;
  secret?: string;
}

interface UserData {
  domain_validation: boolean;
  fandfUser: string;
  fnfDisabled?: boolean;
  id?: string;
  isGoogleAuthActive: boolean;
  isMicrosoftAuthActive: boolean;
  isStandardAuthActive: boolean;
  logo?: string;
  logo_dark?: string;
  eapPhoneNumber?: string;
  emergencyPhoneNumber?: string;
  customFeatureClient?: CustomFeatureClient;
  open?: string;
  singleSignOn: Array<SSO>;
  valid?: string;
  token?: string;
  expiration?: string;
  fname?: string;
  access?: AccessLevel;
  usageId?: string | boolean;
  image?: string;
  clientName?: string;
  npsStatus?: string;
  authType?: string;
  email?: string;
  isHub?: boolean;
  clientid?: string;
  dept?: string;
  userId?: string;
  pamClientCode?: string;
}

/**
 * ClientInfo returned from login API response.
 * Contains client-specific data that should update local storage when user logs in.
 */
export interface ClientInfo {
  clientid: string;
  isStandardAuthActive: boolean;
  isMicrosoftAuthActive: boolean;
  isGoogleAuthActive: boolean;
  sso: Array<SSO>;
  logo?: string;
  logo_dark?: string;
  fnfDisabled?: boolean;
  eapPhoneNumber?: string;
  emergencyPhoneNumber?: string;
  customFeatureClient?: CustomFeatureClient;
  pamClientCode?: string;
  open?: string;
}

export const useUserData = () => {
  const dispatch = useDispatch();
  const userData = useSelector<unknown, UserData | undefined>(
    (state: any) => state.user.userData,
    shallowEqual
  );

  const persistUserData = useCallback(
    (data: UserData) => {
      dispatch(updateUserData(data));
    },
    [dispatch]
  );

  const appendUserData = useCallback(
    (data: Partial<UserData>) => {
      if (userData) {
        const updatedUserData = { ...userData, ...data };

        dispatch(updateUserData(updatedUserData));
      } else {
        dispatch(updateUserData(data));
      }
    },
    [dispatch, userData]
  );

  const clearClientData = useCallback(() => {
    if (userData) {
      const {
        clientid,
        dept,
        fandfUser,
        domain_validation,
        isStandardAuthActive,
        isMicrosoftAuthActive,
        isGoogleAuthActive,
        singleSignOn,
        fnfDisabled,
        logo,
        logo_dark,
        customFeatureClient,
        eapPhoneNumber,
        emergencyPhoneNumber,
        pamClientCode,
        open,
        ...sessionData
      } = userData;

      if (sessionData.token) {
        dispatch(updateUserData(sessionData as UserData));
      } else {
        dispatch(updateUserData(undefined));
      }
    } else {
      localStorage.removeItem('userData');
    }
  }, [dispatch, userData]);

  /**
   * Updates client-specific data in local storage.
   * Used when user logs into an account with a different clientId than stored in local storage.
   */
  const updateClientData = useCallback(
    (clientInfo: ClientInfo) => {
      if (!clientInfo) return;

      const updatedData: Partial<UserData> = {
        clientid: clientInfo.clientid,
        isStandardAuthActive: clientInfo.isStandardAuthActive,
        isMicrosoftAuthActive: clientInfo.isMicrosoftAuthActive,
        isGoogleAuthActive: clientInfo.isGoogleAuthActive,
        singleSignOn: clientInfo.sso,
        logo: clientInfo.logo,
        logo_dark: clientInfo.logo_dark,
        fnfDisabled: clientInfo.fnfDisabled,
        eapPhoneNumber: clientInfo.eapPhoneNumber,
        emergencyPhoneNumber: clientInfo.emergencyPhoneNumber,
        customFeatureClient: clientInfo.customFeatureClient,
        pamClientCode: clientInfo.pamClientCode,
        open: clientInfo.open,
      };

      if (userData) {
        dispatch(updateUserData({ ...userData, ...updatedData }));
      } else {
        dispatch(updateUserData(updatedData as UserData));
      }
    },
    [dispatch, userData]
  );

  return {
    userData,
    persistUserData,
    appendUserData,
    clearClientData,
    updateClientData,
  };
};
